import "core-js/stable";
import "regenerator-runtime/runtime";

import * as Turbo from "@hotwired/turbo";

// Action Text / Trix
import "trix";
import "@rails/actiontext";
window.Turbo = Turbo;

import "../controllers";

require("channels");

import * as bootstrap from "bootstrap";
window.bootstrap = bootstrap;

// Custom Turbo confirm dialog (reemplaza window.confirm nativo del browser)
//
// Los confirms se serializan en cola: el segundo modal solo se muestra
// después de que el primero esté completamente cerrado (animación Bootstrap
// de 300ms). Sin esto, bsModal.show() es un no-op cuando _isTransitioning=true,
// y hidden.bs.modal resuelve el segundo confirm con false, cancelando la acción.
let _confirmQueue = Promise.resolve();

Turbo.config.confirmMethod = (message) => {
  const current = _confirmQueue.then(() => new Promise((resolve) => {
    const modal = document.getElementById("turbo-confirm-modal");
    if (!modal) { resolve(window.confirm(message)); return; }

    modal.querySelector("#turbo-confirm-message").textContent = message;
    const bsModal   = bootstrap.Modal.getOrCreateInstance(modal);
    const btnOk     = modal.querySelector("[data-confirm-ok]");
    const btnCancel = modal.querySelector("[data-confirm-cancel]");

    let done = false;
    const finish = (result) => {
      if (done) return;
      done = true;
      btnOk.removeEventListener("click", handleOk);
      btnCancel.removeEventListener("click", handleCancel);
      modal.removeEventListener("hidden.bs.modal", handleHidden);
      resolve(result);
    };

    // OK: esperar a que el modal se cierre del todo antes de resolver,
    // para que el siguiente confirm pueda abrirlo sin conflicto de animación.
    const handleOk = () => {
      modal.removeEventListener("hidden.bs.modal", handleHidden);
      modal.addEventListener("hidden.bs.modal", () => finish(true), { once: true });
      bsModal.hide();
    };
    const handleCancel = () => bsModal.hide();
    const handleHidden = () => finish(false);

    btnOk.addEventListener("click",     handleOk,     { once: true });
    btnCancel.addEventListener("click", handleCancel, { once: true });
    modal.addEventListener("hidden.bs.modal", handleHidden, { once: true });
    bsModal.show();
  }));

  _confirmQueue = current.catch(() => {});
  return current;
};

import TomSelect from "tom-select";
import "tom-select/dist/css/tom-select.bootstrap5.css";

import "../stylesheets/application";
import "../src/pagy.js";

import LocalTime from "../src/local_time.js";
window.LocalTime = LocalTime;
LocalTime.config.locale = "es";

// Imágenes estáticas
const images = require.context("../images", true);
const imagePath = (name) => images(name, true);

// ---------------------------------------------------------------------------
// TomSelect — inicialización universal para .js-tomselect
// ---------------------------------------------------------------------------
export function initTomSelect(el) {
  if (el.tomselect) return; // ya inicializado

  const useTags    = el.dataset.tags === "true";
  const allowClear = el.dataset.allowClear === "true";
  const ajaxConfig = el.dataset.ajax ? JSON.parse(el.dataset.ajax) : null;

  // Placeholder: usa data-placeholder si existe, si no toma el texto de la
  // primera <option value=""> generada por Rails prompt:/include_blank:
  const emptyOpt   = el.querySelector('option[value=""]');
  const placeholder = el.dataset.placeholder || (emptyOpt ? emptyOpt.textContent.trim() : "");
  // Si hay option vacío (prompt), permitir seleccionarlo para limpiar
  const hasPrompt  = !!emptyOpt;

  // remove_button solo en multi-select; single-select usa clear_button si procede
  const plugins = [];
  if ((allowClear || hasPrompt) && !el.multiple) plugins.push("clear_button");
  if (el.multiple)                               plugins.push("remove_button");

  // La <option value=""> de prompt:/include_blank: NO se trata como valor
  // (allowEmptyOption por defecto = false): si se admitiera, TomSelect la
  // seleccionaría como item en cuanto el campo estuviera vacío y taparía el
  // placeholder. Vaciar el campo se hace con el clear_button.
  const options = {
    plugins,
    placeholder,
    create: useTags,
    maxOptions: null,
    createOnBlur: useTags,
    // Siempre permitir crear aunque AJAX devuelva resultados
    createFilter: useTags ? () => true : null,
  };

  if (ajaxConfig?.url) {
    // Cargar opciones al abrir el campo (sin esperar a que el usuario escriba)
    options.preload = "focus";

    // Opción pre-seleccionada: inyectada por JS para evitar duplicados con el ajax
    const initialValue = el.dataset.initialValue;
    const initialText  = el.dataset.initialText || initialValue;
    if (initialValue) {
      options.options = [{ value: initialValue, text: initialText }];
      options.items   = [initialValue];
    }

    options.load = function (query, callback) {
      const url = new URL(ajaxConfig.url, location.origin);
      url.searchParams.set("term", query);
      fetch(url.toString())
        .then((r) => r.json())
        .then((data) => {
          const results = (data.results || data).map((r) => ({
            value: r.id ?? r.value,
            text:  r.text,
          }));
          callback(results);
        })
        .catch(() => callback());
    };
  }

  new TomSelect(el, options);
}

// ---------------------------------------------------------------------------
// Bootstrap components + TomSelect — inicialización por contenedor
// ---------------------------------------------------------------------------
function initializeComponents(container = document) {
  // Popovers
  container.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => {
    new bootstrap.Popover(el, { sanitize: false });
  });

  // Tooltips
  container.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
    const tip = new bootstrap.Tooltip(el);
    el.addEventListener('click', () => tip.hide());
  });

  // Dropdowns: strategy fixed para escapar contenedores overflow:auto
  container.querySelectorAll('[data-bs-toggle="dropdown"]').forEach((el) => {
    if (!bootstrap.Dropdown.getInstance(el)) {
      new bootstrap.Dropdown(el, { popperConfig: { strategy: "fixed" } });
    }
  });

  // Toasts
  container.querySelectorAll(".toast").forEach((el) => {
    if (!el.classList.contains("shown-toast")) {
      new bootstrap.Toast(el).show();
      el.classList.add("shown-toast");
    }
  });

  // TomSelect para todos los selects marcados
  container.querySelectorAll("select.js-tomselect").forEach((el) => {
    initTomSelect(el);
  });
}

// ---------------------------------------------------------------------------
// Eventos Turbo
// ---------------------------------------------------------------------------
["turbo:load", "turbo:frame-load", "turbo:render"].forEach((evt) => {
  document.addEventListener(evt, () => {
    requestAnimationFrame(() => {
      initializeComponents();
    });
  });
});

// Toasts tras Turbo load (doble rAF para que Stimulus conecte primero)
document.addEventListener("turbo:load", () => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const container = document.getElementById("toast-container");
      if (container) {
        container.querySelectorAll(".toast:not(.shown-toast)").forEach((el) => {
          new bootstrap.Toast(el).show();
          el.classList.add("shown-toast");
        });
      }
    });
  });
});

// Turbo Streams — reinicializar componentes en el target actualizado
document.addEventListener("turbo:before-stream-render", (event) => {
  const fallbackToDefaultActions = event.detail.render;
  event.detail.render = function (streamElement) {
    fallbackToDefaultActions(streamElement);
    setTimeout(() => {
      const targetId = streamElement.getAttribute("target");
      const target = document.getElementById(targetId);
      if (target) initializeComponents(target);
      initializeComponents(document.getElementById("toast-container"));
    }, 0);
  };
});

// ---------------------------------------------------------------------------
// Eventos vanilla JS (sustitutos de los delegados jQuery)
// ---------------------------------------------------------------------------

// Tabs Bootstrap 5: quitar active de hermanos
document.addEventListener("shown.bs.tab", (event) => {
  event.target
    .closest(".nav-pills")
    ?.querySelectorAll(":scope > a, :scope > li > a")
    .forEach((a) => {
      if (a !== event.target) a.classList.remove("active");
    });
});

// Clicks en [data-href] → abrir en nueva pestaña
document.addEventListener("click", (event) => {
  const el = event.target.closest("[data-href]");
  if (el) window.open(el.dataset.href, "_blank");
});

// ---------------------------------------------------------------------------
// Keyboard shortcuts
// ---------------------------------------------------------------------------

// '/' → foco en el buscador de la top bar (como GitHub/Linear)
document.addEventListener("keydown", (event) => {
  const tag = document.activeElement?.tagName;
  if (event.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(tag) && !event.ctrlKey && !event.metaKey) {
    const searchInput = document.querySelector(".cat__top-bar__search input");
    if (searchInput) {
      event.preventDefault();
      searchInput.focus();
      searchInput.select();
    }
  }
});

// Escape → desenfoca el buscador si está activo
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && document.activeElement?.closest(".cat__top-bar__search")) {
    document.activeElement.blur();
  }
});

// ? → abre el modal de atajos de teclado
document.addEventListener("keydown", (event) => {
  const tag = document.activeElement?.tagName;
  if (event.key === "?" && !["INPUT", "TEXTAREA", "SELECT"].includes(tag) && !event.ctrlKey && !event.metaKey) {
    const el = document.getElementById("keyboard-shortcuts-modal");
    if (el) bootstrap.Modal.getOrCreateInstance(el).show();
  }
});

// Auto-foco en el primer campo cuando se abre el panel de filtros
document.addEventListener("shown.bs.collapse", (event) => {
  if (!event.target.id?.includes("filter-panel")) return;
  const firstInput = event.target.querySelector("input:not([type='hidden']):not([type='checkbox']), select");
  if (firstInput) firstInput.focus();
});

// ---------------------------------------------------------------------------
// Barra de progreso + loading state en botones submit
// Contador de peticiones en vuelo para soportar envíos simultáneos.
//
// Problema: Turbo llama a progressBar.hide() internamente (p.ej. en
// BrowserAdapter.visitCompleted tras stream renders o navegaciones),
// ignorando si hay peticiones en vuelo. Lo interceptamos para que
// solo oculte cuando el contador sea 0.
// ---------------------------------------------------------------------------
let _submitsInFlight = 0;
let _hideTimer = null;
const _bar = Turbo.navigator.delegate.adapter.progressBar;
const _realHide = _bar.hide.bind(_bar);

// Interceptar TODAS las llamadas a hide() — las nuestras y las internas de Turbo
_bar.hide = function () {
  if (_submitsInFlight === 0) _realHide();
};

function _scheduleHide() {
  if (_hideTimer) clearTimeout(_hideTimer);
  _hideTimer = setTimeout(() => {
    _hideTimer = null;
    if (_submitsInFlight === 0) { _bar.setValue(1); _realHide(); }
  }, 100);
}

document.addEventListener("turbo:submit-start", (event) => {
  _submitsInFlight++;
  if (_hideTimer) { clearTimeout(_hideTimer); _hideTimer = null; }
  _bar.setValue(0);
  _bar.show();

  const form = event.detail.formSubmission?.formElement;
  if (!form) return;
  form.querySelectorAll('[type="submit"]').forEach((btn) => {
    if (btn.dataset.noLoading !== undefined) return;
    btn.disabled = true;
    btn.dataset._html = btn.innerHTML;
    const label = btn.textContent.trim();
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>${label}`;
  });
});

document.addEventListener("turbo:submit-end", (event) => {
  _submitsInFlight = Math.max(0, _submitsInFlight - 1);
  if (_submitsInFlight === 0) _scheduleHide();

  const form = event.detail.formSubmission?.formElement;
  if (!form) return;
  form.querySelectorAll('[type="submit"]').forEach((btn) => {
    btn.disabled = false;
    if (btn.dataset._html) {
      btn.innerHTML = btn.dataset._html;
      delete btn.dataset._html;
    }
  });
});

// ---------------------------------------------------------------------------
// Restaurar turbo-frames perezosos al navegar atrás
// ---------------------------------------------------------------------------
document.addEventListener("turbo:render", (event) => {
  if (event.detail.renderMethod === "restore") {
    document.querySelectorAll("turbo-frame[src]").forEach((frame) => frame.reload());
  }
});

window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    document.querySelectorAll("turbo-frame[src]").forEach((frame) => frame.reload());
  }
});

// ---------------------------------------------------------------------------
// Limpieza antes del cache Turbo
// ---------------------------------------------------------------------------
document.addEventListener("turbo:before-cache", () => {
  // TomSelect
  document.querySelectorAll("select.js-tomselect").forEach((el) => {
    if (el.tomselect) el.tomselect.destroy();
  });

  // Toasts
  document.querySelectorAll(".toast").forEach((el) => el.remove());

  // Bootstrap modals: cerrar instancias y eliminar backdrops a la fuerza
  document.querySelectorAll(".modal.show").forEach((el) => {
    bootstrap.Modal.getInstance(el)?.hide();
    el.classList.remove("show");
    el.style.display = "";
    el.removeAttribute("aria-modal");
    el.setAttribute("aria-hidden", "true");
  });
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");

  // Bootstrap dropdowns abiertos
  document.querySelectorAll(".dropdown-menu.show").forEach((el) => {
    el.classList.remove("show");
  });
  document.querySelectorAll("[data-bs-toggle='dropdown'].show, [aria-expanded='true']").forEach((el) => {
    el.classList.remove("show");
    el.setAttribute("aria-expanded", "false");
  });

  // Tooltips
  document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
    bootstrap.Tooltip.getInstance(el)?.dispose();
  });

  // Menú móvil
  document.body.classList.remove("cat__menu-left--visible--mobile");
});
